import React, { createContext, useContext, useState, ReactNode } from "react";

// Define the shape of the context
interface ImpersonationContextType {
  impersonatedUserId: string | null;
  setImpersonatedUserId: (userId: string | null) => void;
  clearImpersonation: () => void;
}

// Create the context
const ImpersonationContext = createContext<
  ImpersonationContextType | undefined
>(undefined);

// Provider component
export const ImpersonationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [impersonatedUserId, setImpersonatedUserIdState] = useState<
    string | null
  >(null);

  const setImpersonatedUserId = (userId: string | null) => {
    setImpersonatedUserIdState(userId);
    if (userId) {
      localStorage.setItem("impersonatedUserId", userId);
    } else {
      localStorage.removeItem("impersonatedUserId");
    }
  };

  const clearImpersonation = () => {
    setImpersonatedUserId(null);
  };

  // Load from localStorage on initial mount
  React.useEffect(() => {
    const stored = localStorage.getItem("impersonatedUserId");
    if (stored) setImpersonatedUserIdState(stored);
  }, []);

  return (
    <ImpersonationContext.Provider
      value={{ impersonatedUserId, setImpersonatedUserId, clearImpersonation }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};

// Custom hook for usage in components
export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error(
      "useImpersonation must be used within an ImpersonationProvider",
    );
  }
  return context;
};
